// Global in-call screen. Mounts once in the app shell next to IncomingCallToast
// and owns the UI for ANY active call — outbound (from a "voice-call-started"
// event any dial entry point emits) or inbound-answered (from "voice-connect").
//
// The controls (mute / speaker / keypad / hold / hang up) are rendered from the
// FIRST frame and never move: Connecting → Ringing → Connected only changes the
// state line's text, so there is no layout jump when the call connects.
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Mic, MicOff, Volume2, Grid3x3, Pause, Play, PhoneOff, Loader2,
} from "lucide-react";
import { readCallOutcome } from "../../services/clickToCall";
import "./ActiveCall.css";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

const fmt = (secs) => {
  const s = Math.max(0, Math.floor(secs));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

const sidOf = (conn) => {
  const p = conn?.parameters || conn?.customParameters || {};
  return p.CallSid || p.callSid || p.call_sid || "";
};

const numberFrom = (conn) => {
  const p = conn?.parameters || conn?.customParameters || {};
  return p.From || p.Caller || p.from || p.To || p.to || "";
};

const OUTCOME_LABELS = {
  answered: "Call ended", completed: "Call ended", connected: "Call ended",
  no_answer: "No answer", "no-answer": "No answer", busy: "Busy",
  failed: "Call failed", voicemail: "Voicemail", canceled: "Call ended",
};

// Why recording is OFF (C/gulftech-f4). Showing the reason avoids an owner
// filing a bug against the dot — especially no_disclosure_version_on_file,
// where recording is DELIBERATELY refused on all-party-consent grounds.
const REC_REASON = {
  tenant_has_no_recording_settings: "Recording isn’t set up for this tenant.",
  recording_disabled_for_tenant: "Recording is turned off for this tenant.",
  mode_is_not_record_all: "Recording isn’t set to record every call.",
  no_disclosure_version_on_file:
    "Recording is off — no consent disclosure is on file (recording all calls requires one).",
};

export default function ActiveCall() {
  const [active, setActive] = useState(false);
  const [callee, setCallee] = useState({ name: "", number: "" });
  const [phase, setPhase] = useState("connecting"); // connecting|ringing|connected|ended
  const [seconds, setSeconds] = useState(0);
  const [muted, setMuted] = useState(false);
  const [held, setHeld] = useState(false);
  const [speaker, setSpeaker] = useState(false);
  const [showKeypad, setShowKeypad] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recReason, setRecReason] = useState(null);
  const [reconnecting, setReconnecting] = useState(false);
  const [outcome, setOutcome] = useState("");

  const connRef = useRef(null);
  const timerRef = useRef(null);
  const acceptedRef = useRef(false);

  const stopTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  const reset = useCallback(() => {
    stopTimer();
    connRef.current = null;
    acceptedRef.current = false;
    setActive(false);
    setPhase("connecting");
    setSeconds(0);
    setMuted(false); setHeld(false); setSpeaker(false);
    setShowKeypad(false); setRecording(false); setOutcome("");
  }, [stopTimer]);

  const end = useCallback(() => {
    stopTimer();
    setPhase("ended");
    const sid = sidOf(connRef.current);
    if (sid) {
      // Read the authoritative server-derived outcome for the end-of-call line.
      setTimeout(async () => {
        const o = await readCallOutcome(sid);
        if (o) setOutcome(o);
      }, 2000);
    }
    setTimeout(() => reset(), 3500);
  }, [stopTimer, reset]);

  const attach = useCallback((conn, { connected = false } = {}) => {
    connRef.current = conn;
    acceptedRef.current = connected;
    const startTimer = () => {
      setSeconds(0); stopTimer();
      timerRef.current = setInterval(() => setSeconds((p) => p + 1), 1000);
    };
    if (connected) { setPhase("connected"); startTimer(); }
    try {
      conn.on?.("ringing", () => setPhase("ringing"));
      conn.on?.("accept", () => { acceptedRef.current = true; setPhase("connected"); startTimer(); });
      conn.on?.("disconnect", () => end());
      conn.on?.("cancel", () => end());
      conn.on?.("reject", () => end());
      conn.on?.("error", () => end());
    } catch { /* non-fatal */ }
  }, [end, stopTimer]);

  useEffect(() => {
    const onStart = (e) => {
      const { connection, callee: cal } = e.detail || {};
      if (!connection) return;
      setCallee({ name: cal?.name || "", number: cal?.number || numberFrom(connection) });
      setPhase("connecting"); setSeconds(0);
      setMuted(false); setHeld(false); setSpeaker(false);
      setShowKeypad(false); setOutcome("");
      // Explicit per-call flag wins; otherwise the tenant default from register.
      setRecording(cal?.recording != null ? Boolean(cal.recording) : Boolean(window.__voiceRecordingDefault));
      setRecReason(window.__voiceRecordingReason || null);
      setActive(true);
      attach(connection);
    };
    // Inbound answered: the device fires connect with the live connection.
    const onConnect = (e) => {
      const connection = e?.detail?.connection;
      if (!connection || connRef.current === connection) return;
      setCallee({ name: "", number: numberFrom(connection) });
      setActive(true);
      attach(connection, { connected: true });
    };
    const onDisconnect = () => { if (connRef.current) end(); };
    const onRecording = (e) => {
      setRecording(Boolean(e?.detail?.recording));
      setRecReason(e?.detail?.reason || null);
    };
    const onReconnecting = () => setReconnecting(true);
    const onReconnected = () => setReconnecting(false);

    window.addEventListener("voice-call-started", onStart);
    window.addEventListener("voice-connect", onConnect);
    window.addEventListener("voice-disconnect", onDisconnect);
    window.addEventListener("voice-recording", onRecording);
    window.addEventListener("voice-reconnecting", onReconnecting);
    window.addEventListener("voice-reconnected", onReconnected);
    window.addEventListener("voice-registered", onReconnected);
    return () => {
      window.removeEventListener("voice-call-started", onStart);
      window.removeEventListener("voice-connect", onConnect);
      window.removeEventListener("voice-disconnect", onDisconnect);
      window.removeEventListener("voice-recording", onRecording);
      window.removeEventListener("voice-reconnecting", onReconnecting);
      window.removeEventListener("voice-reconnected", onReconnected);
      window.removeEventListener("voice-registered", onReconnected);
    };
  }, [attach, end]);

  useEffect(() => () => stopTimer(), [stopTimer]);

  const hangUp = () => { try { connRef.current?.disconnect?.(); } catch { /* ignore */ } };
  const toggleMute = () => {
    const next = !muted;
    try { connRef.current?.mute?.(next); } catch { /* ignore */ }
    setMuted(next);
    if (!next) setHeld(false);
  };
  // Client-side hold: the SDK has no native hold, so hold mutes the mic and
  // marks the call held (the voice lane adds true server hold-music later).
  const toggleHold = () => {
    const next = !held;
    try { connRef.current?.mute?.(next); } catch { /* ignore */ }
    setHeld(next); setMuted(next);
  };
  const toggleSpeaker = () => {
    const next = !speaker;
    try {
      const dev = window.__voiceDevice;
      dev?.audio?.speakerDevices?.set?.(next ? "default" : undefined);
    } catch { /* output routing is browser-limited; keep the visual state */ }
    setSpeaker(next);
  };
  const sendDigit = (k) => { try { connRef.current?.sendDigits?.(k); } catch { /* ignore */ } };

  if (!active) return null;

  const stateLine =
    reconnecting ? "Reconnecting…"
    : phase === "connecting" ? "Connecting…"
    : phase === "ringing" ? "Ringing…"
    : phase === "connected" ? fmt(seconds)
    : phase === "ended" ? (OUTCOME_LABELS[outcome] || "Call ended")
    : "";

  const title = callee.name || callee.number || "Call";
  const sub = callee.name && callee.number ? callee.number : "";
  const canHangUp = phase !== "ended";

  return (
    <div className="ac-overlay" role="dialog" aria-label="Active call">
      <div className="ac-card">
        <div className="ac-head">
          <div className="ac-who">
            <span className="ac-name">{title}</span>
            {sub ? <span className="ac-number">{sub}</span> : null}
          </div>
          {recording ? (
            <span className="ac-rec" title="This call is being recorded">
              <span className="ac-rec-dot" aria-hidden /> REC
            </span>
          ) : recReason ? (
            <span
              className={`ac-norec ${recReason === "no_disclosure_version_on_file" ? "is-consent" : ""}`}
              title={REC_REASON[recReason] || "This call is not being recorded."}
            >
              Not recording
            </span>
          ) : null}
        </div>

        <div className={`ac-state ac-state--${phase}${reconnecting ? " is-reconnecting" : ""}`} aria-live="polite">
          {reconnecting ? <Loader2 size={13} className="ac-spin" /> : null}
          {stateLine}
        </div>

        {/* Controls — rendered from the first frame; positions never change. */}
        <div className="ac-controls">
          <button type="button" className={`ac-ctl ${muted ? "is-on" : ""}`} onClick={toggleMute} aria-pressed={muted}>
            {muted ? <MicOff size={18} /> : <Mic size={18} />}
            <span>{muted ? "Unmute" : "Mute"}</span>
          </button>
          <button type="button" className={`ac-ctl ${speaker ? "is-on" : ""}`} onClick={toggleSpeaker} aria-pressed={speaker}>
            <Volume2 size={18} />
            <span>Speaker</span>
          </button>
          <button type="button" className={`ac-ctl ${showKeypad ? "is-on" : ""}`} onClick={() => setShowKeypad((s) => !s)} aria-pressed={showKeypad}>
            <Grid3x3 size={18} />
            <span>Keypad</span>
          </button>
          <button type="button" className={`ac-ctl ${held ? "is-on" : ""}`} onClick={toggleHold} aria-pressed={held}>
            {held ? <Play size={18} /> : <Pause size={18} />}
            <span>{held ? "Resume" : "Hold"}</span>
          </button>
          <button type="button" className="ac-ctl ac-ctl--hangup" onClick={hangUp} disabled={!canHangUp} aria-label="Hang up">
            <PhoneOff size={18} />
            <span>End</span>
          </button>
        </div>

        {showKeypad ? (
          <div className="ac-keypad">
            {KEYS.map((k) => (
              <button key={k} type="button" className="ac-key" onClick={() => sendDigit(k)}>{k}</button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
